import {
	faArrowRightToBracket,
	faBan,
	faCartPlus,
	faMinus,
	faPlus,
	faSpinner,
	faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthGate } from '../hooks/useAuthGate';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import * as cartService from '../services/portalCartService';
import { useCartStore } from '../stores/cartStore';

/**
 * Common quantity presets for the dropdown selector.
 * After 10 we show a "10+" option that switches to manual input.
 */
const QUANTITY_PRESETS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

interface AddToCartControlProps {
	itemId: number;
	/**
	 * Display variant:
	 * - 'compact': Small button for product cards in the catalog grid.
	 *   Shows "Add" initially, transforms to inline stepper.
	 * - 'full': Larger control for the product detail page.
	 *   Shows dropdown quantity selector + "Add to Cart" button,
	 *   transforms to stepper after adding.
	 * - 'floating': Round 36px "+" button designed to be absolutely
	 *   positioned on a product image (Instacart / Weee pattern).
	 *   Out of cart: white circle with brand-accent border + plus.
	 *   In cart: same circle becomes a compact pill stepper that
	 *   anchors to the same bottom-right corner so the whole control
	 *   stays on the image and doesn't reflow the card height.
	 */
	variant: 'compact' | 'full' | 'floating';
	/**
	 * Stock availability. `false` disables Add and shows an
	 * "Out of stock" pill in place of the CTA — keeps the user
	 * from queuing a request that backend validation will reject.
	 * `null`/`undefined` means stock is unknown (do not block).
	 * In-cart items still render the stepper so the user can
	 * remove or reduce a previously-added quantity.
	 */
	inStock?: boolean | null;
	/**
	 * Stepper orientation for the in-cart state on the compact variant.
	 * - 'auto' (default): vertical stack on mobile, horizontal on md+.
	 *   Used by the catalog GRID, where each card has horizontal room
	 *   and the stepper sits below the image — vertical buttons give
	 *   real 44pt tap targets on mobile.
	 * - 'horizontal': always horizontal `[−][qty][+]`. Used by the
	 *   catalog LIST view, where the row is already horizontal and a
	 *   3-row vertical stepper would balloon the row height to ~150px.
	 */
	layout?: 'auto' | 'horizontal';
}

/**
 * Unified add-to-cart control used on both the catalog grid and product detail page.
 *
 * Behavior:
 * - If the item is NOT in the cart: shows an "Add" button (compact) or
 *   quantity dropdown + "Add to Cart" (full).
 * - If the item IS in the cart: shows a quantity stepper (- qty +).
 *   Decrementing to 0 removes the item from the cart.
 *
 * All mutations update the cart store so the badge and cart page stay in sync.
 */
export function AddToCartControl({
	itemId,
	variant,
	inStock,
	layout = 'auto',
}: AddToCartControlProps): JSX.Element {
	const { t } = useTranslation();
	const { token } = usePortalAuth();
	const requireAuth = useAuthGate();
	const display = useDisplaySettings();
	const cart = useCartStore((s) => s.cart);
	const setCart = useCartStore((s) => s.setCart);

	const [isLoading, setIsLoading] = useState(false);
	// Pre-add quantity selector state (full variant only)
	const [selectedQty, setSelectedQty] = useState(1);
	// Whether the user chose "10+" and is typing a custom quantity
	const [customQtyMode, setCustomQtyMode] = useState(false);
	const [customQtyValue, setCustomQtyValue] = useState('');

	// Find this item's line in the cart (if it exists)
	const cartLine = useMemo(
		() => cart?.lineItems.find((li) => li.fkItemId === itemId) ?? null,
		[cart, itemId],
	);

	const cartQuantity = cartLine?.quantityOrdered ?? 0;
	const isInCart = cartLine != null && cartQuantity > 0;

	// Reset pre-add state when item gets added to cart
	useEffect(() => {
		if (isInCart) {
			setSelectedQty(1);
			setCustomQtyMode(false);
			setCustomQtyValue('');
		}
	}, [isInCart]);

	const handleAdd = useCallback(async () => {
		if (!requireAuth()) return;
		if (token == null) return;
		setIsLoading(true);
		try {
			const qty = customQtyMode
				? parseInt(customQtyValue, 10) || 1
				: selectedQty;
			const response = await cartService.addToCart(token, {
				itemId,
				quantity: qty,
			});
			if (response.success && response.data != null) {
				setCart(response.data);
			}
		} finally {
			setIsLoading(false);
		}
	}, [token, itemId, selectedQty, customQtyMode, customQtyValue, setCart]);

	// Debounced quantity updates for the stepper — allows rapid clicks
	// or typed input without an API call per change.
	const pendingQtyRef = useRef<number | null>(null);
	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// Local display quantity for optimistic UI while debouncing
	const [localQty, setLocalQty] = useState<number | null>(null);
	const displayQty = localQty ?? cartQuantity;

	// Reset local qty when cart updates from server
	useEffect(() => {
		setLocalQty(null);
	}, [cartQuantity]);

	const flushQuantityUpdate = useCallback(async () => {
		const newQty = pendingQtyRef.current;
		pendingQtyRef.current = null;
		if (token == null || cartLine == null || newQty == null) return;

		setIsLoading(true);
		try {
			if (newQty <= 0) {
				const response = await cartService.removeFromCart(token, cartLine.id);
				if (response.success) {
					setCart(response.data);
				}
			} else {
				const response = await cartService.updateCartItem(token, cartLine.id, {
					quantityOrdered: newQty,
				});
				if (response.success && response.data != null) {
					setCart(response.data);
				}
			}
		} finally {
			setIsLoading(false);
		}
	}, [token, cartLine, setCart]);

	const handleUpdateQuantity = useCallback(
		(newQty: number) => {
			// Optimistic UI update
			setLocalQty(newQty > 0 ? newQty : 0);
			pendingQtyRef.current = newQty;

			// Debounce the API call
			if (debounceTimerRef.current != null) {
				clearTimeout(debounceTimerRef.current);
			}
			debounceTimerRef.current = setTimeout(() => {
				debounceTimerRef.current = null;
				void flushQuantityUpdate();
			}, 500);
		},
		[flushQuantityUpdate],
	);

	// Out-of-stock indicator used when `inStock === false` and the item
	// isn't already in the cart. Disabled pill — readable but not
	// tappable, so the user doesn't queue a request the backend will
	// reject. Items already in cart still render the stepper so the
	// user can reduce or remove them.
	//
	// Visual: a brand-tinted, low-emphasis pill rather than a solid
	// gray block. In dark mode the heavy `gray-800` we tried first
	// landed as a near-black slab on top of the brand-tinted page bg,
	// which read as "broken / error" instead of "muted state".
	// Switching to `bg-brand-input` keeps the pill on-brand (it's the
	// same CSS var used for input fields and other muted surfaces),
	// and the lighter foreground keeps it scannable without competing
	// with the active CTAs around it.
	const renderOutOfStock = (compact: boolean): JSX.Element => {
		const baseClass = compact
			? 'flex items-center justify-center gap-1.5 w-full px-2 py-2 bg-gray-100 dark:bg-brand-input border border-brand-border text-gray-500 dark:text-gray-300 text-xs font-medium rounded-lg cursor-not-allowed min-h-touch whitespace-nowrap'
			: 'flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 dark:bg-brand-input border border-brand-border text-gray-500 dark:text-gray-300 font-medium rounded-lg cursor-not-allowed min-h-touch whitespace-nowrap';
		return (
			<button
				type="button"
				disabled
				aria-disabled="true"
				onClick={(e) => e.stopPropagation()}
				className={baseClass}
			>
				<FontAwesomeIcon
					icon={faBan}
					className={compact ? 'text-[11px]' : ''}
				/>
				<span>{t('Out of stock')}</span>
			</button>
		);
	};

	// Anonymous visitor — replace Add to Cart with a sign-in CTA. The
	// price area on the card/detail already carries the "Sign in to see
	// pricing" copy when prices are hidden, so we deliberately avoid
	// repeating it here:
	//   - pricing visible    → "Sign in to order"  (they see the price)
	//   - pricing hidden     → "Sign in"           (price area handles
	//                                               the "see pricing"
	//                                               message)
	// Final purchase auth still lives in handleAdd → requireAuth.
	const renderAnonymousCta = (compact: boolean): JSX.Element => {
		const label = display.canSeePricing ? t('Sign in to order') : t('Sign in');
		const baseClass = compact
			? 'flex items-center justify-center gap-1.5 w-full px-3 py-2 bg-portal-accent hover:bg-portal-accent-hover text-white text-sm font-medium rounded-lg transition-colors min-h-touch'
			: 'flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg transition-colors min-h-touch';
		return (
			<button
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					requireAuth();
				}}
				className={baseClass}
			>
				<FontAwesomeIcon
					icon={faArrowRightToBracket}
					className={compact ? 'text-xs' : ''}
				/>
				<span>{label}</span>
			</button>
		);
	};

	// Authenticated visitor on a browse-only storefront: there's nothing
	// useful to show here — no Add to Cart, no Sign In, just the absence
	// of a CTA. Returning null lets the card collapse cleanly. The price
	// area still renders.
	if (!display.canPurchase && !display.isAnonymousVisitor) {
		return null;
	}

	// --- COMPACT variant (catalog card) ---
	if (variant === 'compact') {
		// Anonymous visitor on a purchase-gated storefront: render NOTHING
		// at the bottom of each card. The previous per-card "Sign in" /
		// "Sign in to order" button created three problems:
		//   1. It duplicated the lock-icon + "Sign in for pricing" text
		//      that already sits in the price slot above it.
		//   2. It pulled visual weight away from the product image / title,
		//      so cards read as sign-in walls instead of products.
		//   3. It ate ~52px of vertical space per card across the entire
		//      grid — a real density tax on mobile.
		// Card-tap navigates to the product detail page, where a full-
		// width contextual "Sign in to order" CTA does the conversion
		// work in context (the user has shown intent by tapping in).
		if (!display.canPurchase) {
			return null;
		}
		if (!isInCart && inStock === false) {
			return renderOutOfStock(true);
		}
		if (!isInCart) {
			return (
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						void handleAdd();
					}}
					disabled={isLoading}
					className="flex items-center justify-center gap-1.5 w-full px-3 py-2 bg-portal-accent hover:bg-portal-accent-hover text-white text-sm font-medium rounded-lg transition-colors min-h-touch disabled:opacity-60"
				>
					{isLoading ? (
						<FontAwesomeIcon icon={faSpinner} spin className="text-xs" />
					) : (
						// Inherits the button's themed text color (currentColor) —
						// the button's text class is the single source of the
						// on-accent color (PD-13292 review note).
						<FontAwesomeIcon icon={faCartPlus} className="text-xs" />
					)}
					<span>{t('Add')}</span>
				</button>
			);
		}

		// In cart — inline stepper with editable quantity input.
		// Two layout shapes, picked at the call site:
		//   - 'auto' (catalog GRID): vertical stack on mobile, horizontal
		//     on md+. The mobile stack gives full-width 44pt tap targets
		//     because grid cards have lots of room below the image and
		//     the prior condensed horizontal stepper felt like icons
		//     floating on a card. Source order stays `−, qty, +` for
		//     keyboard/screen reader; the visual flip uses `order-*`.
		//   - 'horizontal' (catalog LIST): always horizontal, dense.
		//     The list row is already a horizontal layout with a narrow
		//     action column — a vertical stepper would push each row to
		//     ~150px tall and bury the favorite star above it. The
		//     dense shape is `[−][qty][+]` at h-8 with brand-accent
		//     icons. We trade a few mm of tap target for visual fit;
		//     full-stepper users still have the PDP one tap away.
		// Visual treatment matches the CRM client-shop stepper either
		// way: soft neutral outer container, brand-accent (gold) icons,
		// qty raised in its own card.
		if (layout === 'horizontal') {
			return (
				<div
					className="inline-flex items-center gap-0.5 rounded-lg border border-brand-border bg-brand-surface px-1 py-1 overflow-hidden"
					onClick={(e) => e.stopPropagation()}
				>
					<button
						type="button"
						onClick={() => handleUpdateQuantity(displayQty - 1)}
						disabled={isLoading}
						className="w-7 h-8 flex items-center justify-center text-portal-accent hover:bg-portal-accent/10 rounded transition-colors disabled:opacity-40"
						aria-label={
							displayQty === 1 ? t('Remove from cart') : t('Decrease quantity')
						}
					>
						{isLoading ? (
							<FontAwesomeIcon icon={faSpinner} spin className="text-[11px]" />
						) : displayQty === 1 ? (
							<FontAwesomeIcon icon={faTrash} className="text-[11px]" />
						) : (
							<FontAwesomeIcon icon={faMinus} className="text-[11px]" />
						)}
					</button>
					<input
						type="number"
						min={1}
						value={displayQty}
						onChange={(e) => {
							const val = parseInt(e.target.value, 10);
							if (!isNaN(val) && val >= 1) {
								handleUpdateQuantity(val);
							}
						}}
						onClick={(e) => e.stopPropagation()}
						className="w-12 h-7 text-center text-xs font-semibold text-gray-900 dark:text-gray-100 bg-brand-surface border border-brand-border rounded outline-none focus:ring-2 focus:ring-portal-accent/30 focus:border-portal-accent/40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
						aria-label={t('Quantity')}
					/>
					<button
						type="button"
						onClick={() => handleUpdateQuantity(displayQty + 1)}
						disabled={isLoading}
						className="w-7 h-8 flex items-center justify-center text-portal-accent hover:bg-portal-accent/10 rounded transition-colors disabled:opacity-40"
						aria-label={t('Increase quantity')}
					>
						<FontAwesomeIcon icon={faPlus} className="text-[11px]" />
					</button>
				</div>
			);
		}

		return (
			<div
				className="flex flex-col md:flex-row md:items-center md:justify-between divide-y md:divide-y-0 divide-brand-border rounded-lg border border-brand-border bg-brand-surface md:px-1 md:py-1 overflow-hidden"
				onClick={(e) => e.stopPropagation()}
			>
				<button
					type="button"
					onClick={() => handleUpdateQuantity(displayQty - 1)}
					disabled={isLoading}
					className="order-3 md:order-1 w-full md:w-9 h-11 md:h-9 flex items-center justify-center text-portal-accent hover:bg-portal-accent/10 md:rounded-md transition-colors disabled:opacity-40"
					aria-label={
						displayQty === 1 ? t('Remove from cart') : t('Decrease quantity')
					}
				>
					{isLoading ? (
						<FontAwesomeIcon
							icon={faSpinner}
							spin
							className="text-sm md:text-xs"
						/>
					) : displayQty === 1 ? (
						<FontAwesomeIcon icon={faTrash} className="text-sm md:text-xs" />
					) : (
						<FontAwesomeIcon icon={faMinus} className="text-sm md:text-xs" />
					)}
				</button>
				<input
					type="number"
					min={1}
					value={displayQty}
					onChange={(e) => {
						const val = parseInt(e.target.value, 10);
						if (!isNaN(val) && val >= 1) {
							handleUpdateQuantity(val);
						}
					}}
					onClick={(e) => e.stopPropagation()}
					className="order-2 w-full md:w-14 h-11 md:h-8 text-center text-sm font-semibold text-gray-900 dark:text-gray-100 bg-brand-surface md:border md:border-brand-border md:rounded-md md:shadow-sm outline-none focus:ring-2 focus:ring-portal-accent/30 focus:border-portal-accent/40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
					aria-label={t('Quantity')}
				/>
				<button
					type="button"
					onClick={() => handleUpdateQuantity(displayQty + 1)}
					disabled={isLoading}
					className="order-1 md:order-3 w-full md:w-9 h-11 md:h-9 flex items-center justify-center text-portal-accent hover:bg-portal-accent/10 md:rounded-md transition-colors disabled:opacity-40"
					aria-label={t('Increase quantity')}
				>
					<FontAwesomeIcon icon={faPlus} className="text-sm md:text-xs" />
				</button>
			</div>
		);
	}

	// --- FLOATING variant (overlaid on the catalog product image) ---
	//
	// Instacart / Weee mobile pattern: a round + button sits on the
	// product image bottom-right; tapping it adds 1 to the cart and
	// the same control transforms into a compact pill stepper anchored
	// to the same corner. The whole control stays on the image so the
	// card height doesn't reflow when the customer adds an item — that
	// reflow on a 3-col mobile grid was the most disruptive part of
	// our previous bottom-of-card layout.
	//
	// Anonymous + browse-only storefronts: render nothing. Tapping
	// the card already navigates to the product detail page where the
	// contextual sign-in / sign-up CTA lives.
	if (variant === 'floating') {
		if (!display.canPurchase) {
			return null;
		}
		// Out-of-stock items: disabled gray circle so the card visually
		// reads as "unavailable" rather than as "tappable, will
		// surprise-fail at the API". Items already in the cart still
		// render the stepper so the customer can reduce the quantity
		// even after the item went out of stock.
		if (!isInCart && inStock === false) {
			return (
				<button
					type="button"
					disabled
					aria-disabled="true"
					aria-label={t('Out of stock')}
					onClick={(e) => e.stopPropagation()}
					className="w-9 h-9 rounded-full bg-gray-200 dark:bg-brand-input text-gray-400 dark:text-gray-500 flex items-center justify-center shadow-md cursor-not-allowed"
				>
					<FontAwesomeIcon icon={faBan} className="text-xs" />
				</button>
			);
		}
		if (!isInCart) {
			// FILLED style — same accent-bg + white-icon as the in-cart
			// stepper. The earlier "empty shell" treatment (white bg +
			// accent border + accent +) was visually distinct from the
			// stepper, so the control's appearance jumped on first add.
			// Filled-on-filled means the corner anchor reads as the
			// SAME thing whether the item is in the cart or not —
			// the customer just sees the qty change.
			return (
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						void handleAdd();
					}}
					disabled={isLoading}
					aria-label={t('Add to cart')}
					className="w-9 h-9 rounded-full bg-portal-accent text-white hover:bg-portal-accent-hover flex items-center justify-center shadow-md transition-colors disabled:opacity-60"
				>
					{isLoading ? (
						<FontAwesomeIcon icon={faSpinner} spin className="text-xs" />
					) : (
						<FontAwesomeIcon icon={faPlus} className="text-sm" />
					)}
				</button>
			);
		}
		// In cart — compact pill stepper. Same height as the round
		// "+" so the corner anchor doesn't visually shift on add. The
		// pill widens to the right (`right-0` placement on the parent
		// keeps the right edge fixed) so the stepper grows into the
		// product image instead of off the card edge.
		return (
			<div
				className="inline-flex items-center h-9 rounded-full bg-portal-accent text-white shadow-md overflow-hidden"
				onClick={(e) => e.stopPropagation()}
			>
				<button
					type="button"
					onClick={() => handleUpdateQuantity(displayQty - 1)}
					disabled={isLoading}
					aria-label={
						displayQty === 1 ? t('Remove from cart') : t('Decrease quantity')
					}
					className="w-9 h-9 flex items-center justify-center hover:bg-black/10 transition-colors"
				>
					{isLoading ? (
						<FontAwesomeIcon icon={faSpinner} spin className="text-xs" />
					) : displayQty === 1 ? (
						<FontAwesomeIcon icon={faTrash} className="text-xs" />
					) : (
						<FontAwesomeIcon icon={faMinus} className="text-xs" />
					)}
				</button>
				{/* PD-13692: type-in quantity on the grid card (parity with the
				    list view + PDP, which already allow direct entry). w-12 fits
				    3-4 digits (PD-13574). The global input[type=number] base
				    style (border/shadow/brand-input bg/dark text) outranks plain
				    utilities by selector specificity, so the in-pill input forces
				    transparent bg + white text + no border/shadow via !important
				    to match the accent pill's +/- ends in both resting and
				    focused states. */}
				<input
					type="number"
					min={1}
					value={displayQty}
					onChange={(e) => {
						const val = parseInt(e.target.value, 10);
						if (!isNaN(val) && val >= 1) {
							handleUpdateQuantity(val);
						}
					}}
					onClick={(e) => e.stopPropagation()}
					className="w-12 h-9 text-center text-sm font-semibold tabular-nums !bg-transparent !text-white !border-0 !shadow-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
					aria-label={t('Quantity')}
				/>
				<button
					type="button"
					onClick={() => handleUpdateQuantity(displayQty + 1)}
					disabled={isLoading}
					aria-label={t('Increase quantity')}
					className="w-9 h-9 flex items-center justify-center hover:bg-black/10 transition-colors"
				>
					<FontAwesomeIcon icon={faPlus} className="text-xs" />
				</button>
			</div>
		);
	}

	// --- FULL variant (product detail page) ---
	if (!display.canPurchase) {
		return renderAnonymousCta(false);
	}
	if (!isInCart && inStock === false) {
		return renderOutOfStock(false);
	}
	if (!isInCart) {
		return (
			<div className="flex items-stretch gap-3">
				{/* Quantity selector — dropdown or custom input */}
				<div className="shrink-0">
					{customQtyMode ? (
						<input
							type="number"
							min={1}
							value={customQtyValue}
							onChange={(e) => setCustomQtyValue(e.target.value)}
							placeholder={t('Qty')}
							autoFocus
							className="w-20 h-12 rounded-lg border border-brand-border bg-brand-surface text-center text-sm font-medium text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-portal-accent focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
						/>
					) : (
						<select
							value={selectedQty}
							onChange={(e) => {
								const val = e.target.value;
								if (val === 'custom') {
									setCustomQtyMode(true);
									setCustomQtyValue('');
								} else {
									setSelectedQty(parseInt(val, 10));
								}
							}}
							className="h-12 rounded-lg border border-brand-border bg-brand-surface px-3 pr-8 text-sm font-medium text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-portal-accent focus:border-transparent appearance-none cursor-pointer"
							style={{
								backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E")`,
								backgroundPosition: 'right 0.5rem center',
								backgroundRepeat: 'no-repeat',
								backgroundSize: '1.25em 1.25em',
							}}
						>
							{QUANTITY_PRESETS.map((qty) => (
								<option key={qty} value={qty}>
									{qty}
								</option>
							))}
							<option value="custom">{t('10+')}</option>
						</select>
					)}
				</div>

				{/* Add to Cart button */}
				<button
					type="button"
					onClick={() => void handleAdd()}
					disabled={isLoading}
					className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg transition-colors min-h-touch disabled:opacity-60 disabled:cursor-not-allowed"
				>
					{isLoading ? (
						<FontAwesomeIcon icon={faSpinner} spin />
					) : (
						// Inherits the button's themed text color (currentColor) —
						// the button's text class is the single source of the
						// on-accent color (PD-13292 review note).
						<FontAwesomeIcon icon={faCartPlus} />
					)}
					<span>{t('Add to Cart')}</span>
				</button>
			</div>
		);
	}

	// In cart — show stepper with editable input (full size). Same
	// visual treatment as the compact variant: soft neutral outer
	// container, accent-colored icon buttons, and the quantity raised
	// into its own white card. Larger sizing for the product detail
	// surface so the touch targets stay min-touch (44px).
	return (
		<div className="flex items-center gap-4">
			<div className="flex items-center gap-1 border border-brand-border bg-brand-surface rounded-lg px-1.5 py-1.5">
				<button
					type="button"
					onClick={() => handleUpdateQuantity(displayQty - 1)}
					disabled={isLoading}
					className="w-11 h-11 flex items-center justify-center text-portal-accent hover:bg-portal-accent/10 rounded-md transition-colors disabled:opacity-40 min-w-touch min-h-touch"
					aria-label={
						displayQty === 1 ? t('Remove from cart') : t('Decrease quantity')
					}
				>
					{isLoading ? (
						<FontAwesomeIcon icon={faSpinner} spin className="text-sm" />
					) : displayQty === 1 ? (
						<FontAwesomeIcon icon={faTrash} className="text-sm" />
					) : (
						<FontAwesomeIcon icon={faMinus} className="text-sm" />
					)}
				</button>
				<input
					type="number"
					min={1}
					value={displayQty}
					onChange={(e) => {
						const val = parseInt(e.target.value, 10);
						if (!isNaN(val) && val >= 1) {
							handleUpdateQuantity(val);
						}
					}}
					className="w-16 h-10 text-center text-base font-semibold text-gray-900 dark:text-gray-100 bg-brand-surface border border-brand-border rounded-md shadow-sm outline-none focus:ring-2 focus:ring-portal-accent/30 focus:border-portal-accent/40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
					aria-label={t('Quantity')}
				/>
				<button
					type="button"
					onClick={() => handleUpdateQuantity(displayQty + 1)}
					disabled={isLoading}
					className="w-11 h-11 flex items-center justify-center text-portal-accent hover:bg-portal-accent/10 rounded-md transition-colors disabled:opacity-40 min-w-touch min-h-touch"
					aria-label={t('Increase quantity')}
				>
					<FontAwesomeIcon icon={faPlus} className="text-sm" />
				</button>
			</div>
			<span className="text-sm text-portal-accent font-medium">
				{t('In Cart')}
			</span>
		</div>
	);
}
